import {
  GetPropertyAlertsResponseDto,
  getProperty as getPropertyV2,
  getPropertyUtilities as getPropertyUtilitiesV2,
  PatchPropertyResponseDto,
  updateProperty as updatePropertyV2,
} from '@helios/frontend-api-v2-client';
import { LegacyShapedAssociatedEntity as AssociatedEntity } from '@palmetto/associated-entity';
import request, { requestV2 } from '../';
import { RatePlanOptionsResponse } from '../utilityRatePlan/types';
import {
  BatteryType,
  EnergyType,
  LifetimeImpactApiResponse,
  Property,
  PropertyPatchRequest,
  PublicTariff,
  SourceService,
  SundialEnergyConnectionTypes,
  UpdateBuildingAttributes,
  Utility,
} from './types';

export const PROPERTIES_ENDPOINT = '/properties';

export const getProperty = async (propertyId: string): Promise<Property> => {
  const { data } = await getPropertyV2({ path: { id: propertyId }, throwOnError: true });

  return {
    _id: data._id,
    address: data.address,
    building: data.building as Property['building'],
    associatedEntities: data.associatedEntities as AssociatedEntity[],
    hasEnergyAdvisorData: data.hasEnergyAdvisorData,
    hasGridConnection: data.hasGridConnection,
    electricUtility: data.electricUtility,
    solarSystemActivationDate: data.solarSystemActivationDate,
    sundialSite: data.sundialSite
      ? {
          timeZone: data.sundialSite.timeZone,
          dataSources: (data.sundialSite.dataSources ?? []).map((ds) => ({
            sourceType: ds.sourceType as EnergyType | BatteryType,
            sourceService: ds.sourceService as SourceService,
            sourceId: ds.sourceId,
          })),
          connections: (data.sundialSite.connections ?? []) as SundialEnergyConnectionTypes[],
          earliestReadingTimestamp: data.sundialSite.earliestReadingTimestamp,
          mostRecentReadingTimestamp: data.sundialSite.mostRecentReadingTimestamp,
          firstMeasurableGenerationTimestamp: data.sundialSite.firstMeasurableGenerationTimestamp,
        }
      : undefined,
  };
};

export const getPropertyAlerts = async (propertyId: string): Promise<GetPropertyAlertsResponseDto> => {
  const result = await requestV2({
    url: `${PROPERTIES_ENDPOINT}/${propertyId}/alerts`,
  });
  return result.data;
};

export const updateBuildingAttributes = async ({
  propertyId,
  buildingAttributes,
}: UpdateBuildingAttributes): Promise<void> => {
  await requestV2({
    method: 'PATCH',
    url: `${PROPERTIES_ENDPOINT}/${propertyId}/building`,
    data: { buildingAttributes },
  });
};

export const getPropertyTariffs = async (propertyId: string, utilityId?: string): Promise<PublicTariff[]> => {
  const result = await requestV2({
    url: `${PROPERTIES_ENDPOINT}/${propertyId}/tariffs`,
    params: utilityId ? { utilityId } : undefined,
  });
  return result.data?.data ?? [];
};

export const getUtilityRatePlanOptions = async (
  propertyId: string,
  hasStorage: boolean,
): Promise<RatePlanOptionsResponse> => {
  const params = new URLSearchParams({ hasStorage: String(hasStorage) });
  const result = await requestV2({ url: `${PROPERTIES_ENDPOINT}/${propertyId}/rate-plan-options?${params}` });
  return result.data;
};

export const getLifetimeImpact = async (propertyId: string): Promise<LifetimeImpactApiResponse> => {
  const result = await request({ url: `${PROPERTIES_ENDPOINT}/${propertyId}/lifetime-impact` });
  return result.data;
};

export const getPropertyUtilities = async (propertyId: string): Promise<Utility[]> => {
  const { data } = await getPropertyUtilitiesV2({ path: { id: propertyId }, throwOnError: true });
  return data;
};

export const patchProperty = async (
  propertyId: string,
  { electricUtility, address }: PropertyPatchRequest,
): Promise<PatchPropertyResponseDto> => {
  const { data } = await updatePropertyV2({
    path: { id: propertyId },
    body: { electricUtility, address },
    throwOnError: true,
  });

  return data;
};
